import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { ArrowUpRight, ShieldCheck, Star, Users, Wallet, Sparkles, Leaf, Camera } from "lucide-react";
import { getRecentlyViewed } from "@/lib/recentlyViewed";
import { ProductCard } from "@/components/ProductCard";
import { NewArrivalsCarousel } from "@/components/NewArrivalsCarousel";

const HERO_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/files/lookers/branding/hero_pub_garden.jpg`;
const SELL_BANNER_IMG = "https://images.unsplash.com/photo-1616560787699-7d1261c2675c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const SAVINGS_LIFESTYLE_IMG = "https://images.unsplash.com/photo-1566492031773-4f4e44671857?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const UGC_IMAGES = [
  "https://images.unsplash.com/photo-1525786210598-d527194d3e9a?crop=entropy&cs=srgb&fm=jpg&q=80&w=700",
  "https://images.unsplash.com/photo-1617039346892-d72191157374?crop=entropy&cs=srgb&fm=jpg&q=80&w=700",
  "https://images.unsplash.com/photo-1513673054901-2b5f51551112?crop=entropy&cs=srgb&fm=jpg&q=80&w=700",
  "https://images.unsplash.com/photo-1554250660-d8ba2d397fed?crop=entropy&cs=srgb&fm=jpg&q=80&w=700",
  "https://images.unsplash.com/photo-1574422282888-4c30455ccdcf?crop=entropy&cs=srgb&fm=jpg&q=80&w=700",
  "https://images.unsplash.com/photo-1696623410967-08bc3733e053?crop=entropy&cs=srgb&fm=jpg&q=80&w=700",
  "https://images.unsplash.com/photo-1605464765759-55272601601e?crop=entropy&cs=srgb&fm=jpg&q=80&w=700",
  "https://images.unsplash.com/photo-1524255684952-d7185b509571?crop=entropy&cs=srgb&fm=jpg&q=80&w=700",
];

const PROOF_BADGES = [
  { img: "/badges/wa-owned.png", lines: ["WA OWNED", "+ OPERATED"] },
  { img: "/badges/highly-rated.png", lines: ["HIGHLY RATED ON", "MARKETPLACE"] },
  { img: "/badges/super-seller.png", lines: ["FACEBOOK", "SUPER SELLER"] },
];

const COMMITMENTS = [
  { icon: ShieldCheck, title: "Real Frames", desc: "Every designer frame is hand-inspected and checked for authenticity before it's ever listed. No replicas, no imitations, no knowingly counterfeit frames — if we can't satisfy ourselves it's genuine, it doesn't make it onto Lookers.", link: "/authenticity", linkLabel: "Our Authenticity Guarantee" },
  { icon: Users, title: "Real Customers", desc: "Everyone featured on this website is an actual Lookers customer — no models, no stock photos. Just real people wearing frames they actually bought from us." },
  { icon: Wallet, title: "Real Savings", desc: "Every price is benchmarked against the genuine Australian retail price — never an inflated \u201Cwas\u201D price. When we show a saving, it's real, verified and calculated on that exact frame." },
];

const SHAPE_ART = {
  Round: "w-10 h-10 rounded-full",
  Oval: "w-11 h-8 rounded-full",
  Square: "w-10 h-9 rounded-[6px]",
  Rectangle: "w-12 h-7 rounded-[4px]",
  "Cat Eye": "w-11 h-8 rounded-tl-[16px] rounded-tr-[10px] rounded-br-[6px] rounded-bl-[4px]",
  Aviator: "w-10 h-10 rounded-tl-[12px] rounded-tr-[12px] rounded-br-[22px] rounded-bl-[18px]",
  Geometric: "w-9 h-9 rounded-[2px] rotate-[10deg]",
  Browline: "w-11 h-8 rounded-t-[4px] rounded-b-[16px] border-t-[3px]",
  Oversized: "w-12 h-11 rounded-[16px]",
};
const SHAPE_ART_DEFAULT = "w-10 h-9 rounded-[8px]";


const WHY_PRELOVED = [
  { icon: Wallet, title: "Better Value", desc: "The same designer names, at a fraction of what they cost new." },
  { icon: Sparkles, title: "More Individual", desc: "Discontinued colours, past-season shapes and frames you won't see on everyone else." },
  { icon: Leaf, title: "Less Waste", desc: "A perfectly good designer frame gets worn again instead of sitting in a drawer." },
];

function ShapeGlyph({ shape }) {
  const lens = SHAPE_ART[shape] || SHAPE_ART_DEFAULT;
  return (
    <div className="flex items-center justify-center text-neutral-900 group-hover:text-teal-400 transition-colors" aria-hidden="true">
      <div className={`border-[1.5px] border-current ${lens}`} />
      <div className="w-3 border-t-[1.5px] border-current" />
      <div className={`border-[1.5px] border-current ${lens}`} />
    </div>
  );
}

function Reveal({ children, delay = 0, className = "", ...rest }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export default function Home() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [showcase, setShowcase] = useState([]);

  useEffect(() => {
    api.get("/home").then((res) => setData(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/reviews/showcase", { params: { limit: 8 } }).then((res) => setShowcase(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const ids = getRecentlyViewed();
    if (!ids.length) return;
    api.get("/products", { params: { ids: ids.join(","), limit: 8 } })
      .then((res) => {
        const byId = Object.fromEntries((res.data.items || []).map((p) => [p.id, p]));
        setRecent(ids.map((id) => byId[id]).filter(Boolean).slice(0, 3));
      })
      .catch(() => {});
  }, []);

  const newArrivals = (data?.just_in || []).slice(0, 3);
  const gridProducts = (data?.designer_for_less || data?.shop_the_look || []).slice(0, 6);
  const moreToLove = (data?.shop_the_look || data?.just_in || data?.designer_for_less || []).slice(0, 3);
  const brands = data?.brands || [];
  const shapeTiles = (data?.shape_counts || []).filter((s) => s.count > 0).slice(0, 8);
  const discovery = recent.length ? recent : moreToLove;

  const savingsPool = [...(data?.designer_for_less || []), ...(data?.just_in || [])].filter((p) => p.original_rrp);
  const avgSavingsPct = savingsPool.length
    ? Math.round(savingsPool.reduce((sum, p) => sum + (p.original_rrp - p.selling_price) / p.original_rrp, 0) / savingsPool.length * 100)
    : null;
  const avgSavingsAmount = savingsPool.length
    ? Math.round(savingsPool.reduce((sum, p) => sum + (p.original_rrp - p.selling_price), 0) / savingsPool.length)
    : null;

  return (
    <div data-testid="homepage">
      {/* HERO */}
      <section className="relative min-h-[100vh] sm:min-h-[92vh] w-full overflow-hidden bg-charcoal" data-testid="hero-section">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/10 to-charcoal/40" />

        <div className="relative z-10 h-full flex flex-col justify-between px-5 sm:px-10 lg:px-16 pt-28 sm:pt-32 pb-10 gap-14">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6" data-testid="hero-rating-eyebrow">
              <div className="flex gap-0.5 text-teal-400">
                {Array.from({ length: 4 }).map((_, i) => <Star key={i} size={15} className="fill-teal-400" />)}
                <span className="relative inline-block" style={{ width: 15, height: 15 }}>
                  <Star size={15} className="absolute inset-0 fill-teal-400/25 text-teal-400/25" />
                  <span className="absolute inset-0 overflow-hidden" style={{ clipPath: "inset(0 10% 0 0)" }}>
                    <Star size={15} className="fill-teal-400 text-teal-400" />
                  </span>
                </span>
              </div>
              <span className="text-white/90 text-xs font-heading font-bold uppercase tracking-wider">4.9 / 5 · Marketplace Seller Rating</span>
            </div>
            <h1 className="font-heading font-semibold text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-[-0.03em] mb-6 max-w-[16ch]" data-testid="hero-headline">
              Genuine <span className="font-hand font-normal text-teal-400 lowercase text-6xl sm:text-7xl lg:text-8xl align-baseline tracking-normal">preloved</span> designer frames that deserve a second look.
            </h1>
            <p className="text-white/85 text-sm sm:text-base max-w-lg mb-4" data-testid="hero-subtitle">
              Discover authentic eyewear from some of the world's most iconic brands like Ray-Ban, Gucci, Prada, Tiffany &amp; Co, Versace and many more — at up to 70% less than their original retail price.
            </p>
            <p className="text-teal-400 text-base sm:text-lg font-heading font-bold uppercase tracking-wide" data-testid="hero-tagline">
              Premium eyewear. Preloved prices.
            </p>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-6 sm:gap-8" data-testid="hero-proof-badges">
              {PROOF_BADGES.map((b) => (
                <div key={b.lines.join(" ")} className="flex items-center gap-3" data-testid={`hero-badge-${b.lines[0].toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <img src={b.img} alt="" aria-hidden="true" className="h-14 w-14 shrink-0 object-contain" />
                  <span className="text-white/90 text-xs font-heading font-bold uppercase tracking-wide leading-tight">
                    {b.lines[0]}<br />{b.lines[1]}
                  </span>
                </div>
              ))}
            </div>
            <Link to="/shop" className="inline-flex items-center gap-2 bg-teal-600 hover:bg-white text-white hover:text-charcoal font-heading font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-full transition-all hover:scale-[1.02] active:scale-95 shadow-lg shadow-teal-600/30 mt-10 sm:mt-12" data-testid="hero-shop-now-btn">
              Shop Now <ArrowUpRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* BRAND MARQUEE */}
      <section className="bg-charcoal py-6 overflow-hidden border-b border-white/10" data-testid="brand-marquee-section">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...brands, ...brands].map((b, i) => (
            <Link key={`${b.slug}-${i}`} to={`/brands/${b.slug}`} className="mx-8 shrink-0 opacity-60 hover:opacity-100 transition-opacity" data-testid={i < brands.length ? `marquee-brand-${b.slug}` : undefined}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${b.logo_url_white}`} alt={b.name} className="h-8 sm:h-10 w-auto object-contain" />
            </Link>
          ))}
        </div>
      </section>

      {/* NEW ARRIVALS - large sliding showcase */}
      <section className="py-14 sm:py-20" data-testid="new-arrivals-section">
        <div className="container-lookers">
          <Reveal className="flex items-end justify-between mb-6">
            <div>
              <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-2">Look What's Just Landed</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight">New Arrivals</h2>
              <p className="text-sm text-neutral-500 mt-2">Fresh frames. Only one of each.</p>
            </div>
            <Link to="/shop?sort=newest" className="hidden sm:flex items-center gap-2 text-sm font-heading font-bold uppercase text-charcoal border-b-2 border-charcoal hover:border-teal-600 hover:text-teal-600 transition-colors" data-testid="new-arrivals-view-all">
              Shop New Arrivals <ArrowUpRight size={15} />
            </Link>
          </Reveal>
          <Reveal>
            <NewArrivalsCarousel products={newArrivals} />
          </Reveal>
        </div>
      </section>

      {/* OUR COMMITMENT */}
      <section className="bg-[#F8F8F9] py-14 sm:py-20" data-testid="our-commitment-section">
        <div className="container-lookers">
          <Reveal className="mb-10">
            <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-2">Our Commitment</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight">No Fakes. No Filters. No Fine Print.</h2>
            <p className="text-sm text-neutral-500 mt-2">What you see on Lookers is real.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-6 sm:divide-x sm:divide-neutral-200">
            {COMMITMENTS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08} className="sm:px-8 first:sm:pl-0" data-testid={`commitment-card-${i}`}>
                <c.icon size={30} className="text-teal-600 mb-4" strokeWidth={1.75} />
                <h3 className="font-heading font-extrabold text-lg text-neutral-900 mb-2">{c.title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed line-clamp-4">{c.desc}</p>
                {c.link && (
                  <Link to={c.link} className="inline-flex items-center gap-1 mt-3 text-xs font-heading font-bold uppercase tracking-wide text-teal-700 hover:text-teal-800 transition-colors" data-testid={`commitment-link-${i}`}>
                    {c.linkLabel} <ArrowUpRight size={13} />
                  </Link>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT GRID - pairs of glasses */}
      <section className="py-16 sm:py-24 bg-[#F8F8F9]" data-testid="designer-for-less-section">
        <div className="container-lookers">
          <Reveal className="mb-10">
            <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-2">Up To 70% Below Retail</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight">Premium Frames. Preloved Prices.</h2>
            <p className="text-sm text-neutral-500 mt-2">The designer name stays. The designer price doesn't.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            {gridProducts.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          <Reveal className="text-center mt-14">
            <Link to="/shop" className="inline-flex items-center gap-2 bg-charcoal text-white font-heading font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-full hover:bg-teal-600 transition-all hover:scale-[1.02] active:scale-95" data-testid="shop-all-frames-link">
              Shop All Frames <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SHOP BY BRAND - large logos */}
      <section className="py-16 sm:py-24 bg-white" data-testid="browse-brands-section">
        <div className="container-lookers">
          <Reveal className="flex items-end justify-between mb-10">
            <div>
              <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-2">{brands.length ? `${brands.length} Iconic Houses` : "Iconic Houses"}</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight">Shop By Brand</h2>
            </div>
            <Link to="/brands" className="hidden sm:flex items-center gap-2 text-sm font-heading font-bold uppercase text-charcoal border-b-2 border-charcoal hover:border-teal-600 hover:text-teal-600 transition-colors" data-testid="view-all-brands-link">
              All Brands <ArrowUpRight size={15} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {brands.map((b) => (
              <Link key={b.slug} to={`/brands/${b.slug}`} className="group bg-[#F8F8F9] hover:bg-charcoal rounded-2xl flex flex-col items-center justify-center gap-4 py-10 px-4 transition-colors duration-300" data-testid={`shop-by-brand-tile-${b.slug}`}>
                <img src={`${process.env.REACT_APP_BACKEND_URL}${b.logo_url_black}`} alt={b.name} className="h-10 w-auto object-contain max-w-[130px] group-hover:hidden" />
                <img src={`${process.env.REACT_APP_BACKEND_URL}${b.logo_url_white}`} alt={b.name} className="h-10 w-auto object-contain max-w-[130px] hidden group-hover:block" />
                <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-neutral-400 group-hover:text-teal-400" data-testid={`shop-by-brand-stock-${b.slug}`}>{b.product_count} In Stock</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CHOOSE YOUR LOOK */}
      {shapeTiles.length > 0 && (
        <section className="py-16 sm:py-24 bg-[#F8F8F9]" data-testid="choose-your-look-section">
          <div className="container-lookers">
            <Reveal className="flex items-end justify-between mb-10">
              <div>
                <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-2">Shop By Shape</div>
                <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight">Choose Your Look</h2>
                <p className="text-sm text-neutral-500 mt-2">Know the shape that suits you? Start there.</p>
              </div>
              <Link to="/shop" className="hidden sm:flex items-center gap-2 text-sm font-heading font-bold uppercase text-charcoal border-b-2 border-charcoal hover:border-teal-600 hover:text-teal-600 transition-colors" data-testid="choose-your-look-view-all">
                Browse All Shapes <ArrowUpRight size={15} />
              </Link>
            </Reveal>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {shapeTiles.map((s, i) => (
                <Reveal key={s.shape} delay={i * 0.05}>
                  <Link
                    to={`/shop?shape=${encodeURIComponent(s.shape)}`}
                    className="group bg-white hover:bg-charcoal border border-transparent hover:border-white/20 rounded-2xl h-full flex flex-col items-center justify-center gap-4 py-9 px-3 transition-colors duration-300"
                    data-testid={`shape-tile-${s.shape.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <ShapeGlyph shape={s.shape} />
                    <div className="text-center">
                      <div className="font-heading font-extrabold text-sm uppercase tracking-wide text-neutral-900 group-hover:text-white transition-colors">{s.shape}</div>
                      <div className="text-[11px] font-mono font-bold uppercase tracking-wider text-neutral-400 group-hover:text-teal-400 mt-1">{s.count} In Stock</div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DEALS & SAVINGS BANNER */}
      <section className="bg-charcoal py-16 sm:py-20" data-testid="savings-banner-section">
        <div className="container-lookers grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal className="order-2 lg:order-1">
            <div className="text-teal-400 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-3">Real, Calculated Savings</div>
            <h2 className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.98] mb-5" data-testid="avg-savings-headline">
              {avgSavingsPct ? (
                <>Save an average of <span className="text-teal-400">{avgSavingsPct}%</span> on genuine designer frames</>
              ) : (
                <>Up to <span className="text-teal-400">70% below</span> optical retail</>
              )}
            </h2>
            <p className="text-white/70 text-base max-w-md mb-8">
              Every price is benchmarked against the original Australian retail price (RRP) — not a made-up "was" price.
              {avgSavingsAmount ? ` On average, customers save around ${money(avgSavingsAmount)} per pair.` : ""} Genuine frames, individually checked, at a fraction of their original cost.
            </p>
            <Link to="/shop" className="inline-flex items-center gap-2 bg-teal-600 hover:bg-white text-white hover:text-charcoal font-heading font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-full transition-all hover:scale-[1.02] active:scale-95" data-testid="savings-shop-link">
              Shop The Savings <ArrowUpRight size={16} />
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5]">
              <img src={SAVINGS_LIFESTYLE_IMG} alt="A customer wearing genuine preloved designer frames" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
              {avgSavingsPct && (
                <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur rounded-2xl px-5 py-3" data-testid="savings-image-badge">
                  <div className="font-mono font-extrabold text-3xl text-charcoal leading-none">{avgSavingsPct}%</div>
                  <div className="text-[10px] font-heading font-bold uppercase tracking-wider text-neutral-500 mt-1">Average Saving</div>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE PRELOVED */}
      <section className="py-14 sm:py-18 bg-charcoal" data-testid="why-preloved-section">
        <div className="container-lookers">
          <Reveal className="mb-9">
            <div className="text-teal-400 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-2">The Case For A Second Look</div>
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-white tracking-tight">Why Choose Preloved?</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 sm:divide-x sm:divide-white/10">
            {WHY_PRELOVED.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06} className="sm:px-7 first:sm:pl-0" data-testid={`why-preloved-card-${i}`}>
                <p.icon size={24} className="text-teal-400 mb-3" strokeWidth={1.9} />
                <h3 className="font-heading font-extrabold text-base text-white mb-1.5">{p.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RECENTLY VIEWED / DISCOVERY */}
      {discovery.length > 0 && (
        <section className="py-16 sm:py-24 bg-[#F8F8F9]" data-testid="keep-looking-section">
          <div className="container-lookers">
            <Reveal className="flex items-end justify-between mb-10">
              <div>
                <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-2">{recent.length ? "Picked Up Where You Left Off" : "Still Browsing?"}</div>
                <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight" data-testid="discovery-heading">{recent.length ? "Recently Viewed" : "Keep Looking"}</h2>
                <p className="text-sm text-neutral-500 mt-2">{recent.length ? "Only one of each — if it's still here, it's still available." : "A few more frames worth a second look."}</p>
              </div>
              <Link to="/shop" className="hidden sm:flex items-center gap-2 text-sm font-heading font-bold uppercase text-charcoal border-b-2 border-charcoal hover:border-teal-600 hover:text-teal-600 transition-colors" data-testid="keep-looking-view-all">
                View All <ArrowUpRight size={15} />
              </Link>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {discovery.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* SELL YOUR FRAMES */}
      <section className="grid grid-cols-1 lg:grid-cols-2" data-testid="sell-your-frames-section">
        <div className="flex items-center bg-white order-2 lg:order-1">
          <Reveal className="px-6 sm:px-12 lg:px-16 py-16 max-w-lg">
            <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-4">Sell To Lookers</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-neutral-900 tracking-tight mb-5">Got Designer Frames Gathering Dust?</h2>
            <p className="text-neutral-600 text-sm sm:text-base mb-6">
              Turn frames you no longer wear into cash or Lookers credit. Tell us the brand and condition, and we'll make you an offer.
            </p>
            <Link to="/sell" className="inline-flex items-center gap-2 bg-charcoal text-white font-heading font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-full hover:bg-teal-600 transition-all hover:scale-[1.02] active:scale-95" data-testid="sell-your-frames-cta">
              Get An Offer <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
        <div className="aspect-[4/3] lg:aspect-auto lg:h-[480px] overflow-hidden order-1 lg:order-2">
          <img src={SELL_BANNER_IMG} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      </section>

      {/* LOOKERS IN THE WILD */}
      <section id="lookers-in-the-wild" className="py-16 sm:py-24 bg-[#F8F8F9]" data-testid="ugc-mosaic-section">
        <div className="container-lookers">
          <Reveal className="max-w-2xl mb-10">
            <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-2">Our Customers Are Real Lookers</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight">Real Customers. Real Photos.</h2>
            <p className="text-sm text-neutral-500 mt-3">
              Everyone we feature is an actual Lookers customer wearing frames they bought from us — no models, no stock photography.
              Send us yours and we'll take 15% off your next pair.
            </p>
            <Link to="/share-your-look" className="inline-flex items-center gap-2 mt-5 bg-charcoal text-white font-heading font-bold text-xs uppercase tracking-wide px-6 py-3.5 rounded-full hover:bg-teal-600 transition-all hover:scale-[1.02] active:scale-95" data-testid="ugc-share-cta">
              <Camera size={14} /> Share Your Look · Get 15% Off
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(showcase.length >= 4 ? showcase : UGC_IMAGES.map((src, i) => ({ id: `fallback-${i}`, photo: src }))).map((item, i) => (
              <Reveal key={item.id} delay={i * 0.04} className="aspect-square overflow-hidden rounded-xl group relative" data-testid={`ugc-photo-${i}`}>
                <img
                  src={item.photo?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${item.photo}` : item.photo}
                  alt={item.product ? `${item.first_name} wearing ${item.product.brand_name} ${item.product.model}` : ""}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {item.product && (
                  <Link to={`/product/${item.product.slug}`} className="absolute inset-x-0 bottom-0 bg-charcoal/75 text-white text-[10px] font-heading font-bold uppercase tracking-wider px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {item.product.brand_name} {item.product.model}
                  </Link>
                )}
              </Reveal>
            ))}
          </div>
          {showcase.length < 4 && (
            <p className="text-xs text-neutral-400 mt-4">Lifestyle imagery shown until our own customer gallery grows.</p>
          )}
        </div>
      </section>

      {/* NEWSLETTER */}
      <NewsletterSection />
    </div>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await api.post("/newsletter", { email });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="py-16 sm:py-20 bg-white border-t border-neutral-100" data-testid="newsletter-section">
      <div className="container-lookers text-center max-w-lg">
        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-neutral-900 tracking-tight mb-3">New Drop Alerts</h2>
        <p className="text-neutral-600 text-sm mb-7">There's only ever one of each frame. Be first to know when new designer pairs land.</p>
        <form onSubmit={submit} className="flex gap-2 max-w-sm mx-auto">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            className="flex-1 bg-[#F8F8F9] rounded-full px-5 py-3 text-sm outline-none placeholder:text-neutral-400 text-neutral-900 border border-neutral-200 focus:border-teal-500 transition-colors"
            data-testid="newsletter-email-input"
          />
          <button type="submit" className="bg-charcoal text-white text-xs font-heading font-bold uppercase tracking-wide rounded-full px-5 hover:bg-teal-600 transition-all shrink-0" data-testid="newsletter-submit-button">
            {status === "loading" ? "..." : "Join"}
          </button>
        </form>
        {status === "success" && <p className="text-xs text-teal-600 mt-3" data-testid="newsletter-success-message">You're on the list.</p>}
      </div>
    </section>
  );
}
